// src/components/RequestDownloads.js
import { useEffect, useState } from 'react'
import { useMutation, useQuery } from 'react-query'
import {
  postBusiness,
  postPeople,
  postProperty,
  postProduct
} from '../../Api/index' // Add postProduct here
import { getDownloadsById } from '../../Api/index.js'
import useStore from '../Store/Store'
import businessStore from '../BusinessDataDR15/BusinessStore/Store.js'
import useStorePeople from '../StorePeople/Store.js'
import useStoreProduct from '../ProductDataDR/ProductStore/Store.js'

// Hook to create a download and show alerts via setAlert
export const useCreateDownloadMutation = (setAlert, query, dataType) => {
  const setIsQueryLoading = useStore(state => state.setIsQueryLoading)
  const setDownloadQueryStore = useStore(state => state.setDownloadQueryStore)

  return useMutation(
    ({ query }) =>
      dataType === 'property'
        ? postProperty(query)
        : dataType === 'business'
        ? postBusiness(query)
        : dataType === 'people'
        ? postPeople(query)
        : dataType === 'product'
        ? postProduct(query)
        : Promise.reject('Invalid dataType'),
    {
      onMutate: async () => {
        setIsQueryLoading(true)
      },
      onSuccess: data => {
        if (data?.data?.errors || data.status >= 400) {
          const errMsg = data?.data?.errors?.[0] ?? 'An error occurred'

          // “too many records” error
          if (
            errMsg ===
            '`num_records` requested will exceed credit allotment for plan.'
          ) {
            setAlert({
              open: true,
              title: 'Requesting too many records',
              body: "The number of records you're requesting exceeds the available records remaining on your plan. Try narrowing your search or upgrade your account for more record credits.",
              color: '#000000',
              actions: [
                {
                  label: 'Upgrade account',
                  onClick: () =>
                    (window.location.href = '/settings/billing/subscription')
                },
                {
                  label: 'Ok',
                  onClick: () => setAlert(a => ({ ...a, open: false }))
                }
              ]
            })
            return
          }

          // other errors...
          setAlert({
            open: true,
            title: 'Error',
            body: errMsg,
            color: '#EF3688',
            actions: [
              {
                label: 'Ok',
                onClick: () => setAlert(a => ({ ...a, open: false }))
              }
            ]
          })
          return
        }

        // success path...
        setDownloadQueryStore({ query, id: data.data.id })
        setAlert({
          open: true,
          title: 'Download started',
          body: 'Wait for it to finish here or view the Downloads page to check its progress.',
          color: '#48B966',
          actions: [
            {
              label: 'Go to Downloads',
              onClick: () => (window.location.href = '/download')
            },
            {
              label: 'Ok',
              onClick: () => setAlert(a => ({ ...a, open: false }))
            }
          ]
        })
      },

      onError: error => {
        setAlert({
          open: true,
          title: 'Error',
          body: 'An error occurred',
          color: '#EF3688',
          actions: [
            {
              label: 'Ok',
              onClick: () => setAlert(a => ({ ...a, open: false }))
            }
          ]
        })
      },
      onSettled: () => {
        setIsQueryLoading(false)
      }
    }
  )
}

// Fetch a single download by its ID
const fetchDownloadById = async downloadId => {
  const data = await getDownloadsById(downloadId)
  return data
}

// Hook to poll for download status and stop when complete
export const useDownloadDataById = downloadId => {
  const { setShouldRefetch: setShouldRefetchProperty } = useStore()
  const { setShouldRefetch: setShouldRefetchPeople } = useStorePeople()
  const { setShouldRefetch: setShouldRefetchBusiness } = businessStore()
  const { setShouldRefetch: setShouldRefetchProduct } = useStoreProduct()

  const [isPolling, setIsPolling] = useState(true)

  const query = useQuery(
    ['downloadData', downloadId],
    () => fetchDownloadById(downloadId),
    {
      enabled: !!downloadId,
      refetchInterval: isPolling ? 3000 : false
    }
  )

  useEffect(() => {
    if (query.data?.data?.status === 'completed') {
      setIsPolling(false)
      setShouldRefetchBusiness(true)
      setShouldRefetchPeople(true)
      setShouldRefetchProduct(true)
      setShouldRefetchProperty(true)
    }
  }, [
    query.data,
    setShouldRefetchBusiness,
    setShouldRefetchPeople,
    setShouldRefetchProduct,
    setShouldRefetchProperty
  ])

  return query
}
